import React, { useCallback, useEffect } from 'react'
import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'

import { TtButton, TtIcon, TtTrayChyronOnCamera } from '@23/ui-react'

import {
  removeOverlay,
  setChyron,
  useOverlayState,
} from '../../../../firebase/hooks/useOverlays'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import {
  setActiveCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../firebase/hooks/useTray'
import { type PropsWithTrayItemChildren } from '../TrayItem'
import {
  useLiveDataQuery,
  useUpdateOnCameraState,
} from '../../../../global/blocs/useLiveData'

import './ChyronOnCameraCard.scss'

interface OnCameraCardProps {
  id: string
}

const OnCameraCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<OnCameraCardProps>) => {
  const card = useTrayCard(id)
  const cardState = useTrayCardState(id)
  const overlayState = useOverlayState()
  const { data: liveData, isLoading: isLiveDataLoading } = useLiveDataQuery()
  const upddateOnCameraState = useUpdateOnCameraState()

  const isChyronShowing =
    overlayState?.type === ChyronType.J2SIntroduction &&
    overlayState.cardId === id
  const isEnabled = liveData?.allow_guest_speakers_p === true

  // Auto-remove chyron when feature is disabled
  useEffect(() => {
    if (!isEnabled && isChyronShowing) {
      void removeOverlay()
    }
  }, [isEnabled, isChyronShowing])

  const handleEnableGoOnCamera = useCallback(() => {
    upddateOnCameraState(true)
  }, [upddateOnCameraState])

  const handleDisableGoOnCamera = useCallback(() => {
    upddateOnCameraState(false)
  }, [upddateOnCameraState])

  const showChyron = () => {
    if (isEnabled) {
      void setChyron({
        type: ChyronType.J2SIntroduction,
        cardId: id,
      })
    }
  }

  const onCardClickHandler = () => {
    if (isEnabled) {
      showChyron()
      setActiveCard(id)
    }
  }

  if (card?.type !== 'chyron-on-camera') return null

  return (
    <TtTrayChyronOnCamera
      tag={intl.formatMessage({ defaultMessage: 'Go on camera' })}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      onTtClick={(event) => {
        if (event.target === event.currentTarget) {
          onCardClickHandler()
        }
      }}
    >
      {isEnabled && (
        <TtButton
          slot="toolbar"
          label={
            isChyronShowing
              ? intl.formatMessage({
                  defaultMessage: 'Hide Go on camera TvCard',
                })
              : intl.formatMessage({
                  defaultMessage: 'Show Go on camera TvCard',
                })
          }
          color="secondary"
          variant="ghost"
          size="x-small"
          circle
          onClick={isChyronShowing ? removeOverlay : showChyron}
        >
          <TtIcon
            library="one/webinars"
            name={isChyronShowing ? 'visibility-off' : 'visibility-on'}
          />
        </TtButton>
      )}
      {children?.()}
      <div className="go-on-camera-info">
        <div className="suggestion-text">
          <FormattedMessage
            defaultMessage="You can now request access to go on camera.{linebreak}Just press the {icon} button on top of the sidebar."
            values={{
              icon: (
                <span className="suggestion-text-info">
                  <TtIcon className="mic-icon" name="record" library="23" />
                  <FormattedMessage defaultMessage="Go on camera" />
                </span>
              ),
              linebreak: <br />,
            }}
          />
        </div>
        <div className="button-actions">
          <TtButton
            className="chyron-button"
            color="secondary"
            size="small"
            pill
            loading={isLiveDataLoading}
            onClick={
              isEnabled ? handleDisableGoOnCamera : handleEnableGoOnCamera
            }
            variant={isEnabled ? 'outline' : 'default'}
          >
            {isEnabled ? (
              <FormattedMessage defaultMessage="Disable Go on camera" />
            ) : (
              <FormattedMessage defaultMessage="Enable Go on camera" />
            )}
          </TtButton>
        </div>
      </div>
    </TtTrayChyronOnCamera>
  )
}

export { OnCameraCard }
